/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { extractPatternEntities } from 'src/luis/modules/patterns/extractPatternEntities';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';

/**
 * A hook for subscribing to patterns belonging to the
 * current application version. This hook fetches and
 * constructs Patterns objects using the de-normalized
 * intents and entities returned from `useIntents` hook
 * and `useEntities` hooks.
 */
export const usePatterns = () => {
    const { dispatcher, rootStore } = useGlobalState();
    const { isLoaded: areIntentsLoaded, intents } = useIntents();
    const { isLoaded: areEntitiesLoaded, entities } = useEntities();

    const intentsStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.intentStoreHouse;
    const entitiesStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse;
    const patternsStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.patternStoreHouse;

    const isReadyToLoadPatterns = areIntentsLoaded && areEntitiesLoaded;

    React.useEffect(() => {
        if (isReadyToLoadPatterns) {
            dispatcher.dispatch('loadPatterns');
        }
    }, [dispatcher.dispatch, isReadyToLoadPatterns]);

    return {
        intents,
        entities,
        isLoading: !patternsStoreHouse.isLoaded || patternsStoreHouse.isLoading,
        patterns: patternsStoreHouse.items.map(item => {
            const entitiesAndRolesNames = extractPatternEntities(item.text);

            return {
                id: item.id,
                text: item.text,
                labeledIntent: intentsStoreHouse.findById(item.intentId),
                usedEntitiesAndRoles: entitiesAndRolesNames.reduce((acc, extractedEntityName) => {
                    if (extractedEntityName.includes(':')) {
                        const [entityName, roleName] = extractedEntityName.split(':', 2);

                        return [
                            ...acc,
                            entitiesStoreHouse.findByName(entityName),
                            entitiesStoreHouse.findByName(entityName).entityRoleStoreHouse.findByName(roleName)
                        ];
                    }
                    return [...acc, entitiesStoreHouse.findByName(extractedEntityName)];
                }, [])
            };
        })
    };
};
